(() => {
  if (customElements.get('ip-shop')) return;

  class IpShop extends HTMLElement {
    connectedCallback() {
      this.filterButtons = Array.from(this.querySelectorAll('[data-ip-filter]'));
      this.filters = this.querySelector('.ip-shop__filters');
      this.filterToggle = this.querySelector('[data-ip-filter-toggle]');
      this.filterSelectedCount = this.querySelector('[data-ip-filter-selected-count]');
      this.gridHost = this.querySelector('[data-ip-grid-host]');
      this.gridTitle = this.querySelector('[data-ip-grid-title]');
      this.gridSubtitle = this.querySelector('[data-ip-grid-subtitle]');
      this.initialResultsSubtitle = this.gridSubtitle?.textContent?.trim() || '';
      this.secondaryFilters = Array.from(this.querySelectorAll('[data-ip-secondary-filter]'));
      this.moreCategoriesToggle = this.querySelector('[data-ip-more-categories-toggle]');
      this.moreCategoriesLabel = this.querySelector('[data-ip-more-categories-label]');
      this.emptyMessage = this.querySelector('[data-ip-empty-message]');
      this.errorMessage = this.querySelector('[data-ip-error-message]');
      this.paginationWrap = this.querySelector('[data-ip-pagination-wrap]');
      this.loadMoreButton = this.querySelector('[data-ip-load-more]');
      this.pagination = this.querySelector('[data-ip-pagination]');
      this.pageList = this.querySelector('[data-ip-page-list]');
      this.previousButton = this.querySelector('[data-ip-page-previous]');
      this.nextButton = this.querySelector('[data-ip-page-next]');
      this.infiniteSentinel = this.querySelector('[data-ip-infinite-sentinel]');
      this.gridView = this.dataset.gridView;
      this.batchGridView = this.dataset.batchGridView || 'ip-shop-grid-50';
      this.rootUrl = this.dataset.rootUrl || '/';
      this.initialCollection = this.dataset.initialCollection || '';
      this.initialResultsTitle = this.dataset.initialResultsTitle || '';
      this.paginationType = this.dataset.paginationType || 'pages';
      this.productsPerPage = Math.max(1, Number(this.dataset.productsPerPage || 12));
      this.allowMultipleFilters = this.dataset.allowMultipleFilters === 'true';
      this.buttonLabel = this.dataset.buttonLabel || 'Shop now';
      this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      this.abortController = null;
      this.collectionCache = new Map();
      this.selectedCollections = new Set();
      this.combinedCards = [];
      this.combinedMode = false;
      this.hasInteractedWithFilters = false;
      this.isRequesting = false;
      this.currentPage = 1;
      this.totalPages = 1;
      this.nextPage = 0;
      this.moreCategoriesExpanded = false;

      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => this.handleFilterClick(button));
      });

      this.filterToggle?.addEventListener('click', () => this.toggleMobileFilters());
      this.moreCategoriesToggle?.addEventListener('click', () => this.toggleMoreCategories());
      this.loadMoreButton?.addEventListener('click', () => this.loadMore());
      this.pagination?.addEventListener('click', (event) => this.handlePaginationClick(event));

      const initialFragment = this.currentFragment;
      if (initialFragment) {
        this.syncStateFromFragment(initialFragment);
        this.normaliseCardLabels(initialFragment);
      }

      if (this.activeCollection) this.selectedCollections.add(this.activeCollection);
      this.setActiveFilters(this.selectedCollections);
      this.classList.add('is-filter-ready');
      this.setupInfinitePagination();
      this.loadStateFromUrl();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.infiniteObserver?.disconnect();
    }

    get currentFragment() {
      return this.gridHost?.querySelector('[data-ip-grid-fragment]') || null;
    }

    toggleMobileFilters(forceOpen) {
      if (!this.filters || !this.filterToggle) return;

      const shouldOpen = typeof forceOpen === 'boolean'
        ? forceOpen
        : !this.filters.classList.contains('is-open');

      this.filters.classList.toggle('is-open', shouldOpen);
      this.filterToggle.setAttribute('aria-expanded', String(shouldOpen));
    }

    toggleMoreCategories(forceOpen) {
      if (!this.moreCategoriesToggle || !this.secondaryFilters.length) return;

      const shouldOpen = typeof forceOpen === 'boolean'
        ? forceOpen
        : !this.moreCategoriesExpanded;

      this.moreCategoriesExpanded = shouldOpen;
      this.classList.toggle('is-more-categories-open', shouldOpen);
      this.secondaryFilters.forEach((item) => { item.hidden = !shouldOpen; });

      const filterList = this.moreCategoriesToggle.parentElement;
      if (filterList) {
        if (shouldOpen) {
          filterList.append(this.moreCategoriesToggle);
        } else {
          filterList.insertBefore(this.moreCategoriesToggle, this.secondaryFilters[0]);
        }
      }

      this.moreCategoriesToggle.classList.toggle('is-expanded', shouldOpen);
      this.moreCategoriesToggle.setAttribute('aria-expanded', String(shouldOpen));

      if (this.moreCategoriesLabel) {
        this.moreCategoriesLabel.textContent = shouldOpen
          ? this.moreCategoriesToggle.dataset.lessLabel || 'Fewer Categories'
          : this.moreCategoriesToggle.dataset.moreLabel || 'More Categories';
      }
    }

    async handleFilterClick(button) {
      const collectionHandle = button.dataset.collectionHandle;
      if (!collectionHandle || this.isRequesting) return;

      if (!this.allowMultipleFilters) {
        if (collectionHandle === this.activeCollection) return;
        await this.loadCollection(collectionHandle, {
          title: button.dataset.filterLabel || button.dataset.collectionTitle || '',
          page: 1,
          updateHistory: true
        });
        this.toggleMobileFilters(false);
        this.scrollResultsIntoView();
        return;
      }

      const nextSelection = this.getNextSelection(collectionHandle);
      if (this.sameSelection(nextSelection, this.selectedCollections)) return;

      this.hasInteractedWithFilters = true;
      await this.loadSelection(nextSelection, { page: 1, updateHistory: true });
    }

    getNextSelection(collectionHandle) {
      const allProductsHandle = 'all-products';

      if (!this.hasInteractedWithFilters && !this.selectedCollections.has(collectionHandle)) {
        return new Set([collectionHandle]);
      }

      if (collectionHandle === allProductsHandle) return new Set([allProductsHandle]);

      const nextSelection = new Set(this.selectedCollections);
      nextSelection.delete(allProductsHandle);

      if (nextSelection.has(collectionHandle)) {
        if (nextSelection.size > 1) nextSelection.delete(collectionHandle);
      } else {
        nextSelection.add(collectionHandle);
      }

      if (nextSelection.size === 0) nextSelection.add(collectionHandle);
      return nextSelection;
    }

    sameSelection(first, second) {
      return first.size === second.size && Array.from(first).every((handle) => second.has(handle));
    }

    getOrderedHandles(selection) {
      return this.filterButtons
        .map((button) => button.dataset.collectionHandle)
        .filter((handle) => handle && selection.has(handle));
    }

    async loadSelection(selection, { page = 1, updateHistory = false } = {}) {
      const handles = this.getOrderedHandles(selection);
      if (!handles.length) return;

      if (handles.length === 1) {
        const handle = handles[0];
        const button = this.filterButtons.find((item) => item.dataset.collectionHandle === handle);
        await this.loadCollection(handle, {
          title: button?.dataset.filterLabel || button?.dataset.collectionTitle || '',
          page,
          updateHistory: false
        });

        if (updateHistory) this.replaceSelectionInUrl(handles, page);
        return;
      }

      await this.loadCombinedCollections(handles, { page, updateHistory });
    }

    async handlePaginationClick(event) {
      const button = event.target.closest('[data-ip-page]');
      if (!button || button.disabled || this.isRequesting) return;

      const page = Number(button.dataset.ipPage || 0);
      if (!page || page === this.currentPage) return;

      await this.loadNumberedPage(page);
    }

    async loadCollection(collectionHandle, { title = '', page = 1, updateHistory = false } = {}) {
      const signal = this.beginRequest();

      try {
        const fragment = await this.fetchFragment(collectionHandle, page, this.gridView, signal);

        this.gridHost.replaceChildren(fragment);
        this.combinedMode = false;
        this.combinedCards = [];
        this.normaliseCardLabels(fragment);
        this.syncStateFromFragment(fragment, title);
        this.selectedCollections = new Set([collectionHandle]);
        this.setActiveFilters(this.selectedCollections);

        if (updateHistory) this.replaceSelectionInUrl([collectionHandle], page);
        this.announceUpdate([collectionHandle], page);
      } catch (error) {
        if (error.name !== 'AbortError') this.showError();
      } finally {
        this.endRequest();
      }
    }

    async loadCombinedCollections(collectionHandles, { page = 1, updateHistory = false } = {}) {
      const signal = this.beginRequest();

      try {
        const collections = await Promise.all(
          collectionHandles.map((handle) => this.fetchEntireCollection(handle, signal))
        );
        const uniqueCards = new Map();

        collections.forEach((entry) => {
          entry.cards.forEach((card) => {
            if (!uniqueCards.has(card.id)) uniqueCards.set(card.id, card.html);
          });
        });

        this.combinedMode = true;
        this.combinedCards = Array.from(uniqueCards.values());
        this.selectedCollections = new Set(collectionHandles);
        this.activeCollection = collectionHandles[0];
        this.setActiveFilters(this.selectedCollections);
        this.renderCombinedPage(page);

        if (updateHistory) this.replaceSelectionInUrl(collectionHandles, page);
        this.announceUpdate(collectionHandles, page);
      } catch (error) {
        if (error.name !== 'AbortError') this.showError();
      } finally {
        this.endRequest();
      }
    }

    async fetchEntireCollection(collectionHandle, signal) {
      if (this.collectionCache.has(collectionHandle)) {
        return this.collectionCache.get(collectionHandle);
      }

      const firstFragment = await this.fetchFragment(collectionHandle, 1, this.batchGridView, signal);
      const totalPages = Number(firstFragment.dataset.totalPages || 1);
      const remainingRequests = [];

      for (let page = 2; page <= totalPages; page += 1) {
        remainingRequests.push(this.fetchFragment(collectionHandle, page, this.batchGridView, signal));
      }

      const fragments = [firstFragment, ...(await Promise.all(remainingRequests))];
      const cards = [];

      fragments.forEach((fragment) => {
        fragment.querySelectorAll('[data-ip-product-card]').forEach((card, index) => {
          const id = card.dataset.productId || `${collectionHandle}-${cards.length + index}`;
          cards.push({ id, html: card.outerHTML });
        });
      });

      const result = {
        handle: collectionHandle,
        title: firstFragment.dataset.collectionTitle || collectionHandle,
        cards
      };

      this.collectionCache.set(collectionHandle, result);
      return result;
    }

    async loadNumberedPage(page) {
      if (this.combinedMode) {
        this.renderCombinedPage(page);
        this.replaceSelectionInUrl(this.getOrderedHandles(this.selectedCollections), page);
        this.scrollResultsIntoView();
        this.announceUpdate(this.getOrderedHandles(this.selectedCollections), page);
        return;
      }

      const collectionHandle = this.activeCollection;
      if (!collectionHandle) return;

      const signal = this.beginRequest();

      try {
        const fragment = await this.fetchFragment(collectionHandle, page, this.gridView, signal);

        this.gridHost.replaceChildren(fragment);
        this.normaliseCardLabels(fragment);
        this.syncStateFromFragment(fragment);
        this.replaceSelectionInUrl([collectionHandle], page);
        this.scrollResultsIntoView();
        this.announceUpdate([collectionHandle], page);
      } catch (error) {
        if (error.name !== 'AbortError') this.showError();
      } finally {
        this.endRequest();
      }
    }

    async loadMore() {
      if (this.isRequesting) return;

      if (this.combinedMode) {
        if (!this.nextPage) return;
        this.renderCombinedPage(this.nextPage, { append: true });
        this.announceUpdate(this.getOrderedHandles(this.selectedCollections), this.currentPage);
        return;
      }

      const fragment = this.currentFragment;
      const nextPage = Number(fragment?.dataset.nextPage || 0);
      const collectionHandle = fragment?.dataset.collectionHandle;
      if (!collectionHandle || !nextPage) return;

      const signal = this.beginRequest();

      try {
        const nextFragment = await this.fetchFragment(collectionHandle, nextPage, this.gridView, signal);
        const currentGrid = fragment.querySelector('[data-ip-product-grid]');
        const nextGrid = nextFragment.querySelector('[data-ip-product-grid]');

        if (!currentGrid || !nextGrid) throw new Error('Product grid is missing from the response.');

        currentGrid.append(...Array.from(nextGrid.children));
        this.normaliseCardLabels(currentGrid);
        fragment.dataset.currentPage = nextFragment.dataset.currentPage || '';
        fragment.dataset.totalPages = nextFragment.dataset.totalPages || '';
        fragment.dataset.nextPage = nextFragment.dataset.nextPage || '';
        fragment.dataset.productCount = nextFragment.dataset.productCount || fragment.dataset.productCount || '0';
        this.syncStateFromFragment(fragment);
        this.announceUpdate([collectionHandle], nextPage);
      } catch (error) {
        if (error.name !== 'AbortError') this.showError();
      } finally {
        this.endRequest();
      }
    }

    renderCombinedPage(page, { append = false } = {}) {
      const productCount = this.combinedCards.length;
      this.totalPages = Math.max(1, Math.ceil(productCount / this.productsPerPage));
      this.currentPage = Math.min(Math.max(1, Number(page) || 1), this.totalPages);
      this.nextPage = this.currentPage < this.totalPages ? this.currentPage + 1 : 0;

      const start = (this.currentPage - 1) * this.productsPerPage;
      const cardMarkup = this.combinedCards.slice(start, start + this.productsPerPage).join('');
      const template = document.createElement('template');
      template.innerHTML = cardMarkup;

      if (append && this.currentFragment) {
        const grid = this.currentFragment.querySelector('[data-ip-product-grid]');
        grid?.append(...Array.from(template.content.children));
        this.updateCombinedFragmentData(this.currentFragment, productCount);
        this.normaliseCardLabels(grid || this.currentFragment);
      } else {
        const fragment = document.createElement('div');
        const grid = document.createElement('div');
        fragment.className = 'ip-shop__grid-fragment';
        fragment.setAttribute('data-ip-grid-fragment', '');
        grid.className = 'ip-shop__grid';
        grid.setAttribute('data-ip-product-grid', '');
        grid.append(...Array.from(template.content.children));
        fragment.append(grid);
        this.updateCombinedFragmentData(fragment, productCount);
        this.gridHost.replaceChildren(fragment);
        this.normaliseCardLabels(fragment);
      }

      const selectionTitle = this.getSelectionTitle();
      if (this.gridTitle) this.gridTitle.textContent = selectionTitle;
      this.syncResultsSubtitle('');
      if (this.emptyMessage) this.emptyMessage.hidden = productCount > 0;
      this.renderPagination();
    }

    updateCombinedFragmentData(fragment, productCount) {
      fragment.dataset.collectionHandle = this.getOrderedHandles(this.selectedCollections).join(',');
      fragment.dataset.collectionTitle = this.getSelectionTitle();
      fragment.dataset.productCount = String(productCount);
      fragment.dataset.currentPage = String(this.currentPage);
      fragment.dataset.totalPages = String(this.totalPages);
      fragment.dataset.nextPage = this.nextPage ? String(this.nextPage) : '';
    }

    getSelectionTitle() {
      const labels = this.filterButtons
        .filter((button) => this.selectedCollections.has(button.dataset.collectionHandle))
        .map((button) => button.querySelector('span')?.textContent?.trim() || button.dataset.collectionTitle)
        .filter(Boolean);

      if (labels.length <= 2) return labels.join(' + ');
      return `${labels.length} selected categories`;
    }

    async fetchFragment(collectionHandle, page, view = this.gridView, signal = this.abortController?.signal) {
      const collectionPath = `${this.rootUrl}collections/${encodeURIComponent(collectionHandle)}`;
      const url = new URL(collectionPath, window.location.origin);
      url.searchParams.set('view', view);
      url.searchParams.set('page', String(page));

      const response = await fetch(url.toString(), {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error(`Product request failed with status ${response.status}.`);

      const html = await response.text();
      const responseDocument = new DOMParser().parseFromString(html, 'text/html');
      const fragment = responseDocument.querySelector('[data-ip-grid-fragment]');
      if (!fragment) throw new Error('Product grid is missing from the response.');
      return fragment;
    }

    beginRequest() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.isRequesting = true;
      this.classList.add('is-loading');
      if (this.errorMessage) this.errorMessage.hidden = true;
      this.filterButtons.forEach((button) => { button.disabled = true; });
      if (this.loadMoreButton) this.loadMoreButton.disabled = true;
      this.pagination?.querySelectorAll('button').forEach((button) => { button.disabled = true; });
      return this.abortController.signal;
    }

    endRequest() {
      this.isRequesting = false;
      this.classList.remove('is-loading');
      this.filterButtons.forEach((button) => { button.disabled = false; });
      if (this.loadMoreButton) this.loadMoreButton.disabled = false;
      this.abortController = null;
      this.renderPagination();
    }

    syncStateFromFragment(fragment, titleOverride = '') {
      this.activeCollection = fragment.dataset.collectionHandle || '';
      this.currentPage = Number(fragment.dataset.currentPage || 1);
      this.totalPages = Number(fragment.dataset.totalPages || 1);
      this.nextPage = Number(fragment.dataset.nextPage || 0);

      const collectionTitle = this.getCollectionDisplayTitle(
        this.activeCollection,
        titleOverride || fragment.dataset.collectionTitle || ''
      );
      const productCount = Number(fragment.dataset.productCount || 0);
      if (this.gridTitle && collectionTitle) this.gridTitle.textContent = collectionTitle;
      this.syncResultsSubtitle(this.getResultsSubtitle(this.activeCollection));
      if (this.emptyMessage) this.emptyMessage.hidden = productCount > 0;
      this.renderPagination();
    }

    getCollectionDisplayTitle(collectionHandle, fallbackTitle = '') {
      if (collectionHandle === this.initialCollection && this.initialResultsTitle) {
        return this.initialResultsTitle;
      }

      const filter = this.filterButtons.find((button) => button.dataset.collectionHandle === collectionHandle);
      return filter?.dataset.filterLabel || fallbackTitle;
    }

    getResultsSubtitle(collectionHandle) {
      const filter = this.filterButtons.find((button) => (
        button.dataset.collectionHandle === collectionHandle
      ));
      const categorySubtitle = filter?.dataset.filterSubheading?.trim() || '';

      if (categorySubtitle) return categorySubtitle;
      if (collectionHandle === this.initialCollection) return this.initialResultsSubtitle;
      return '';
    }

    syncResultsSubtitle(text) {
      if (!this.gridSubtitle) return;
      this.gridSubtitle.textContent = text;
      this.gridSubtitle.hidden = !text;
    }

    renderPagination() {
      const hasNextPage = this.nextPage > 0;
      const showLoadMore = this.paginationType === 'load_more' && hasNextPage;
      const showPages = this.paginationType === 'pages' && this.totalPages > 1;
      const showInfinite = this.paginationType === 'infinite' && hasNextPage;

      if (this.loadMoreButton) this.loadMoreButton.hidden = !showLoadMore;
      if (this.pagination) this.pagination.hidden = !showPages;
      if (this.infiniteSentinel) this.infiniteSentinel.hidden = !showInfinite;
      if (this.paginationWrap) this.paginationWrap.hidden = !showLoadMore && !showPages && !showInfinite;
      if (showPages) this.renderPageButtons();
      this.syncInfiniteObserver(showInfinite);
    }

    renderPageButtons() {
      if (!this.pageList || !this.previousButton || !this.nextButton) return;

      this.previousButton.dataset.ipPage = String(this.currentPage - 1);
      this.previousButton.disabled = this.currentPage <= 1 || this.isRequesting;
      this.nextButton.dataset.ipPage = String(this.currentPage + 1);
      this.nextButton.disabled = this.currentPage >= this.totalPages || this.isRequesting;
      this.pageList.replaceChildren();

      this.getVisiblePages().forEach((page) => {
        if (page === 'ellipsis') {
          const ellipsis = document.createElement('span');
          ellipsis.className = 'ip-shop__page-ellipsis';
          ellipsis.textContent = '…';
          ellipsis.setAttribute('aria-hidden', 'true');
          this.pageList.append(ellipsis);
          return;
        }

        const button = document.createElement('button');
        button.className = 'ip-shop__page-button';
        button.type = 'button';
        button.dataset.ipPage = String(page);
        button.setAttribute('aria-label', `Go to page ${page}`);
        button.textContent = String(page);

        if (page === this.currentPage) {
          button.classList.add('is-active');
          button.setAttribute('aria-current', 'page');
          button.disabled = true;
        }

        this.pageList.append(button);
      });
    }

    getVisiblePages() {
      if (this.totalPages <= 7) return Array.from({ length: this.totalPages }, (_, index) => index + 1);

      const pages = new Set([1, this.totalPages, this.currentPage - 1, this.currentPage, this.currentPage + 1]);
      const sortedPages = Array.from(pages)
        .filter((page) => page >= 1 && page <= this.totalPages)
        .sort((a, b) => a - b);
      const visiblePages = [];

      sortedPages.forEach((page, index) => {
        if (index > 0 && page - sortedPages[index - 1] > 1) visiblePages.push('ellipsis');
        visiblePages.push(page);
      });
      return visiblePages;
    }

    setActiveFilters(selection) {
      this.filterButtons.forEach((button) => {
        const isActive = selection.has(button.dataset.collectionHandle);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      if (this.filterSelectedCount) this.filterSelectedCount.textContent = String(selection.size);

      const hasSelectedSecondaryFilter = this.secondaryFilters.some((item) => (
        selection.has(item.dataset.collectionHandle)
      ));
      if (hasSelectedSecondaryFilter) this.toggleMoreCategories(true);
    }

    normaliseCardLabels(container) {
      container.querySelectorAll('[data-ip-card-button]').forEach((button) => {
        button.textContent = button.dataset.available === 'true' ? this.buttonLabel : this.soldOutLabel;
      });
    }

    showError() {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = this.dataset.errorMessage || this.errorMessage.textContent;
      this.errorMessage.hidden = false;
    }

    loadStateFromUrl() {
      const url = new URL(window.location.href);
      const validHandles = new Set(this.filterButtons.map((button) => button.dataset.collectionHandle));
      const requestedCollections = (url.searchParams.get('collections') || '')
        .split(',')
        .filter((handle) => validHandles.has(handle));
      const requestedHandle = url.searchParams.get('collection');
      const requestedPage = this.paginationType === 'pages'
        ? Math.max(1, Number(url.searchParams.get('page') || 1))
        : 1;

      if (this.allowMultipleFilters && requestedCollections.length > 1) {
        this.hasInteractedWithFilters = true;
        this.loadSelection(new Set(requestedCollections), { page: requestedPage, updateHistory: false });
        return;
      }

      const collectionHandle = validHandles.has(requestedHandle)
        ? requestedHandle
        : requestedCollections[0] || this.activeCollection;

      if (!collectionHandle) return;
      if (collectionHandle !== this.activeCollection || requestedPage !== this.currentPage) {
        this.hasInteractedWithFilters = collectionHandle !== this.initialCollection;
        this.loadSelection(new Set([collectionHandle]), { page: requestedPage, updateHistory: false });
      }
    }

    replaceSelectionInUrl(collectionHandles, page = 1) {
      if (window.Shopify?.designMode) return;

      const url = new URL(window.location.href);
      if (collectionHandles.length > 1) {
        url.searchParams.set('collections', collectionHandles.join(','));
        url.searchParams.delete('collection');
      } else {
        url.searchParams.delete('collections');
        if (collectionHandles[0] === this.initialCollection) {
          url.searchParams.delete('collection');
        } else {
          url.searchParams.set('collection', collectionHandles[0]);
        }
      }

      if (this.paginationType === 'pages' && page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }
      window.history.replaceState({}, '', url.toString());
    }

    scrollResultsIntoView() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.querySelector('.ip-shop__results-header')?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    }

    announceUpdate(collectionHandles, page) {
      this.dispatchEvent(new CustomEvent('ip-shop:updated', {
        bubbles: true,
        detail: { collectionHandles, page }
      }));
    }

    setupInfinitePagination() {
      if (this.paginationType !== 'infinite' || !this.infiniteSentinel || !('IntersectionObserver' in window)) return;

      this.infiniteObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !this.isRequesting && this.nextPage) {
          this.loadMore();
        }
      }, { rootMargin: '320px 0px' });
      this.syncInfiniteObserver(this.nextPage > 0);
    }

    syncInfiniteObserver(shouldObserve) {
      if (!this.infiniteObserver || !this.infiniteSentinel) return;
      this.infiniteObserver.disconnect();
      if (shouldObserve) this.infiniteObserver.observe(this.infiniteSentinel);
    }
  }

  customElements.define('ip-shop', IpShop);
})();
